export type BcraNivelRiesgo = 'sinNovedad' | 'normal' | 'atencion' | 'riesgo';

export interface BcraDeudaEntidad {
  entidad?: string | null;
  situacion?: number | null;
  monto?: number | null;
  diasAtrasoPago?: number | null;
  refinanciaciones?: boolean;
  situacionJuridica?: boolean;
  procesoJud?: boolean;
  enRevision?: boolean;
}

export interface BcraChequeRechazado {
  causal?: string | null;
  entidad?: number | null;
  nroCheque: number;
  fechaRechazo?: string | null;
  monto: number;
  fechaPago?: string | null;
  estadoMulta?: string | null;
  enRevision?: boolean;
  procesoJud?: boolean;
}

export interface BcraCentralDeudoresConsulta {
  cuit: string;
  denominacion?: string | null;
  nivelRiesgo: BcraNivelRiesgo;
  mensajeResumen: string;
  situacionMaxima?: number | null;
  periodo?: string | null;
  entidades: BcraDeudaEntidad[];
  chequesRechazados: BcraChequeRechazado[];
  consultaParcial?: boolean;
  errorConsulta?: string | null;
}

export type SnackbarVariant = 'default' | 'error' | 'success' | 'warning' | 'info';

export function normalizarCuitConsulta(value?: string | number | null): string {
  return String(value ?? '').replace(/\D/g, '').slice(0, 11);
}

export function cuitConsultable(value?: string | number | null): boolean {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 11;
}

export function snackbarVariantPorNivelRiesgo(nivel: BcraNivelRiesgo): SnackbarVariant {
  switch (nivel) {
    case 'riesgo':
      return 'error';
    case 'atencion':
      return 'warning';
    case 'normal':
      return 'success';
    case 'sinNovedad':
    default:
      return 'info';
  }
}

export function etiquetaNivelRiesgo(nivel: BcraNivelRiesgo): string {
  switch (nivel) {
    case 'riesgo':
      return 'Riesgo';
    case 'atencion':
      return 'Atención';
    case 'normal':
      return 'Normal';
    case 'sinNovedad':
    default:
      return 'Sin novedad';
  }
}

export function colorIconoPorNivelRiesgo(nivel: BcraNivelRiesgo | null): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (!nivel) return 'default';
  switch (nivel) {
    case 'riesgo':
      return 'error';
    case 'atencion':
      return 'warning';
    case 'normal':
      return 'success';
    case 'sinNovedad':
      return 'info';
    default:
      return 'default';
  }
}

export interface ChequeBcraIngreso {
  nivelRiesgo: BcraNivelRiesgo;
  situacionMaxima?: number | null;
  periodo?: string | null;
  mensajeResumen: string;
  fechaConsulta: string;
  detalle?: BcraCentralDeudoresConsulta;
}

export function buildChequeBcraIngreso(consulta: BcraCentralDeudoresConsulta): ChequeBcraIngreso {
  return {
    nivelRiesgo: consulta.nivelRiesgo,
    situacionMaxima: consulta.situacionMaxima,
    periodo: consulta.periodo,
    mensajeResumen: consulta.mensajeResumen,
    fechaConsulta: new Date().toISOString(),
    detalle: consulta,
  };
}

export function bcraIngresoToConsulta(ingreso: ChequeBcraIngreso | null | undefined): BcraCentralDeudoresConsulta | null {
  if (!ingreso?.nivelRiesgo) return null;
  if (ingreso.detalle) return ingreso.detalle;
  return {
    cuit: '',
    nivelRiesgo: ingreso.nivelRiesgo,
    mensajeResumen: ingreso.mensajeResumen,
    situacionMaxima: ingreso.situacionMaxima,
    periodo: ingreso.periodo,
    entidades: [],
    chequesRechazados: [],
  };
}

export function fechaLimiteChequesBcra12Meses(ref: Date = new Date()): Date {
  const limite = new Date(ref);
  limite.setMonth(limite.getMonth() - 12);
  return limite;
}

export function filtrarChequesBcraUltimos12Meses(
  cheques: BcraChequeRechazado[],
  ref: Date = new Date(),
): BcraChequeRechazado[] {
  const limite = fechaLimiteChequesBcra12Meses(ref);
  return cheques.filter((c) => {
    if (!c.fechaRechazo) return false;
    const fecha = new Date(c.fechaRechazo);
    return !Number.isNaN(fecha.getTime()) && fecha >= limite;
  });
}

export function sumarImporteChequesBcra(cheques: BcraChequeRechazado[]): number {
  return cheques.reduce((acc, c) => acc + (c.monto ?? 0), 0);
}

export function isoFechaDesde12Meses(ref: Date = new Date()): string {
  const limite = fechaLimiteChequesBcra12Meses(ref);
  return limite.toISOString().slice(0, 10);
}
